import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import MarketingFooter from './MarketingFooter';
import MarketingHeader from './MarketingHeader';
import { applyLangFromUrl } from '../../marketing/marketingLangUrl';
import { usePageSeo } from '../../marketing/useMarketingPageMeta';
import '../../pages/landing.css';

type MarketingLayoutProps = {
  children: ReactNode;
  headerVariant?: 'hero' | 'solid';
  title: string;
  description?: string;
  seoPath?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export default function MarketingLayout({
  children,
  headerVariant = 'solid',
  title,
  description,
  seoPath,
  jsonLd,
}: MarketingLayoutProps) {
  const { pathname, search } = useLocation();
  useEffect(() => {
    applyLangFromUrl(search);
  }, [search]);
  usePageSeo({
    title,
    description,
    path: seoPath ?? pathname,
    hreflang: true,
    includeWebPageSchema: Boolean(description),
    robots: 'index, follow',
    jsonLd,
  });
  const [atTop, setAtTop] = useState(headerVariant === 'hero');

  useEffect(() => {
    if (headerVariant !== 'hero') {
      setAtTop(false);
      return;
    }
    const onScroll = () => setAtTop(window.scrollY < 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [headerVariant]);

  return (
    <Box className="landing-page" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MarketingHeader variant={headerVariant} onHero={atTop} />
      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>
      <MarketingFooter />
    </Box>
  );
}
